import { init, addIntegration, captureException as sentryCaptureException, type ErrorEvent, type EventHint } from "@sentry/react"

export function initSentry(): void {
  init({
    dsn: "https://9f8bea8d49bb85b43ff5168058cd34a0@o4511508573454336.ingest.us.sentry.io/4511508627390464",
    environment: import.meta.env.MODE,
    sendDefaultPii: true,
    // Injected wrappers around navigator.serviceWorker.register (Kaspersky's
    // wrsParams shim and lookalikes) throw from code we don't own. Message
    // match catches the ones where the wrapper name leaks into the value.
    ignoreErrors: [/wrsParams/i],
    beforeSend: dropInjectedServiceWorkerNoise,
  })

  if (import.meta.env.MODE === "test" || typeof window === "undefined") return

  // Session Replay is disabled — error + React stack handle the vast majority
  // of debugging needs. To re-enable in error-only mode (recommended baseline:
  // no continuous session recording, only the 60s buffer that flushes when an
  // error fires), uncomment scheduleReplayLoad() below and add these to
  // init({...}) above:
  //   replaysSessionSampleRate: 0,   // no random session sampling
  //   replaysOnErrorSampleRate: 1.0, // capture 60s pre-error on every error
  // The lazy chunk wrapper (src/sentryReplay.ts) and scheduleReplayLoad below
  // are kept in tree so re-enabling is a one-line revert.
  // scheduleReplayLoad()
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function scheduleReplayLoad(): void {
  const load = () => {
    import("./sentryReplay")
      .then(({ replayIntegration }) => addIntegration(replayIntegration()))
      .catch(() => { /* network or ad-blocker fail: no replay this session */ })
  }
  if ("requestIdleCallback" in window) {
    window.requestIdleCallback(load, { timeout: 5000 })
  } else {
    setTimeout(load, 2000)
  }
}

// Drops exceptions that originate entirely inside the auto-generated
// registerSW.js or an injected wrapper around it (antivirus/extension shims
// like Kaspersky's wrsParams). Signature: stack references registerSW.js or
// wrsParams AND no frame is flagged in_app — so real SW errors that touch our
// own code still get through.
function dropInjectedServiceWorkerNoise(
  event: ErrorEvent,
  _hint: EventHint,
): ErrorEvent | null {
  const frames = event.exception?.values?.[0]?.stacktrace?.frames ?? []
  if (frames.length === 0) return event

  const referencesInjectedSW = frames.some((f) => {
    const path = f.filename ?? f.abs_path ?? ""
    const fn = f.function ?? ""
    return path.includes("registerSW.js") || fn.includes("wrsParams") || path.includes("wrsParams")
  })
  const hasInAppFrame = frames.some((f) => f.in_app === true)

  return referencesInjectedSW && !hasInAppFrame ? null : event
}

export const captureException = sentryCaptureException
